import { useMemo } from "react";

import { useOrganizations } from "../../hooks/useOrganizations";
import { INTEGRATION_SCOPES } from "../constants";

import { useFetchConnectedIntegrationAccounts } from "./useFetchConnectedIntegration";

import type { Organization } from "../../types/organization";
import type { CrawlerPreferenceType } from "../types/crawlerPreference";
import type { OrganizationIntegration } from "../types/integrations";

export interface DeploymentWorkspace {
  organization: Organization;
  // Absent until folders have actually been selected — no row exists before that.
  integration?: OrganizationIntegration;
  selectedFolders: string[];
}

// Flattened across preference types and drives, which is how the table lists them.
const selectedFolderNames = (preferences: CrawlerPreferenceType[]) =>
  preferences.flatMap((preference) =>
    preference.data.filter((item) => item.status).map((item) => item.name),
  );

/**
 * The platform accounts connected for a source, and the workspaces configured under the
 * one currently selected.
 *
 * The workspace join has to happen here because organization rows are write-only on the
 * API — they only ever come back nested under their parent account, never addressable on
 * their own.
 *
 * @param source - Source to list accounts for.
 * @param selectedAccountId - verifiable_id of the account in view; falls back to the
 *   first connected account.
 */
export const useDeploymentWorkspaces = (
  source: string,
  selectedAccountId?: string,
) => {
  const accountsQuery = useFetchConnectedIntegrationAccounts(source, {
    integrationScope: INTEGRATION_SCOPES.DEPLOYMENT,
  });
  const organizationsQuery = useOrganizations();

  const accounts = accountsQuery.data ?? [];

  const selectedAccount =
    accounts.find((account) => account.verifiable_id === selectedAccountId) ??
    accounts[0];

  const workspaces = useMemo((): DeploymentWorkspace[] => {
    const children = selectedAccount?.organization_integrations ?? [];
    const childByWorkspace = new Map(
      children.map((child) => [child.organization_uuid, child]),
    );

    return (organizationsQuery.data ?? []).map((organization) => {
      const integration = childByWorkspace.get(organization.organization_uuid);
      return {
        organization,
        integration,
        selectedFolders: integration
          ? selectedFolderNames(integration.preferences ?? [])
          : [],
      };
    });
  }, [organizationsQuery.data, selectedAccount]);

  return {
    accounts,
    selectedAccount,
    workspaces,
    isLoading: accountsQuery.isLoading || organizationsQuery.isLoading,
    isError: accountsQuery.isError || organizationsQuery.isError,
    refetch: () => {
      accountsQuery.refetch();
      organizationsQuery.refetch();
    },
  };
};
